import type * as React from "react";
import {
	IconAutopay,
	IconBank,
	IconBorrowers,
	IconDashboard,
	IconDocuments,
	IconHome,
	IconJobs,
	IconLoans,
	IconPayments,
	IconPerformance,
	IconPortfolio,
	IconReports,
	IconStatements,
} from "~/components/general/BrandIcons";

import { Logo } from "~/components/general/Logo";
import ServicingLockup from "~/components/general/ServicingLockup";
import { NavMain } from "~/components/nav-main";
import { NavUser } from "~/components/nav-user";
import { RoleSwitcher } from "~/components/role-switcher";
import { Separator } from "~/components/ui/separator";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarRail,
	useSidebar,
} from "~/components/ui/sidebar";
import { useIsMobile } from "~/hooks/use-mobile";

type UserRole = "borrower" | "servicer_staff" | "originator_staff" | "investor";

// Navigation items for borrowers (alphabetized)
const borrowerNavItems = [
	{
		icon: IconHome,
		title: "Home",
		url: "/borrower",
	},
	{
		icon: IconLoans,
		title: "Loans",
		url: "/borrower/loans",
	},
	{
		icon: IconPayments,
		title: "Payments",
		url: "/borrower/payments",
	},
	{
		icon: IconStatements,
		title: "Statements",
		url: "/borrower/statements",
	},
	{
		icon: IconDocuments,
		title: "Documents",
		url: "/borrower/documents",
	},
	{
		icon: IconAutopay,
		title: "Autopay",
		url: "/borrower/autopay",
	},
	{
		icon: IconBank,
		title: "Bank Accounts",
		url: "/borrower/settings/bank-accounts",
	},
];

// Navigation items for servicer staff - alphabetized
const servicerStaffNavItems = [
	{
		icon: IconBorrowers,
		title: "Borrowers",
		url: "/servicer/borrowers",
	},
	{
		icon: IconDashboard,
		title: "Dashboard",
		url: "/servicer",
	},
	{
		icon: IconJobs,
		title: "Jobs",
		url: "/servicer/jobs",
	},
	{
		icon: IconLoans,
		title: "Loans",
		url: "/servicer/loans",
	},
	{
		icon: IconPayments,
		title: "Payments",
		url: "/servicer/payments",
	},
	{
		icon: IconReports,
		items: [
			{
				title: "Delinquency",
				url: "/servicer/reports/delinquency",
			},
			{
				title: "Payment History",
				url: "/servicer/reports/payments",
			},
			{
				title: "Portfolio",
				url: "/servicer/reports/portfolio",
			},
		],
		title: "Reports",
		url: "/servicer/reports",
	},
	{
		icon: IconStatements,
		title: "Statements",
		url: "/servicer/statements",
	},
];

// Navigation items for originator staff - alphabetized
const originatorStaffNavItems = [
	{
		icon: IconDashboard,
		title: "Dashboard",
		url: "/originator",
	},
	{
		icon: IconLoans,
		title: "My Loans",
		url: "/originator/loans",
	},
	{
		icon: IconPortfolio,
		title: "Portfolio",
		url: "/originator/portfolio",
	},
	{
		icon: IconReports,
		title: "Reports",
		url: "/originator/reports",
	},
];

// Navigation items for investors - alphabetized
const investorNavItems = [
	{
		icon: IconDashboard,
		title: "Dashboard",
		url: "/investor",
	},
	{
		icon: IconPerformance,
		title: "Performance",
		url: "/investor/performance",
	},
	{
		icon: IconPortfolio,
		title: "Portfolio",
		url: "/investor/portfolio",
	},
	{
		icon: IconStatements,
		title: "Statements",
		url: "/investor/statements",
	},
];

const navigationByRole: Record<UserRole, typeof borrowerNavItems> = {
	borrower: borrowerNavItems,
	investor: investorNavItems,
	originator_staff: originatorStaffNavItems,
	servicer_staff: servicerStaffNavItems,
};

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
	user: {
		name: string;
		email: string;
		avatar?: string;
		roles: UserRole[];
	};
	activeRole?: UserRole;
	onRoleChange?: (role: UserRole) => void;
}

export function AppSidebar({
	user,
	activeRole,
	onRoleChange,
	...props
}: AppSidebarProps) {
	const firstRole = user.roles[0];
	const currentRole = activeRole ?? firstRole ?? "borrower";
	const navItems = navigationByRole[currentRole] ?? borrowerNavItems;
	const isMobile = useIsMobile();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	return (
		<Sidebar collapsible="icon" {...props}>
			<SidebarHeader>
				<div
					className={`flex items-center gap-2 py-2 ${isCollapsed ? "justify-center px-0" : "justify-start px-2"}`}
				>
					{isCollapsed && !isMobile ? (
						<Logo className="h-8" size={32} />
					) : (
						<ServicingLockup className="w-32" />
					)}
				</div>
				{user.roles.length > 1 && onRoleChange && (
					<>
						<Separator className="my-1" />
						<RoleSwitcher
							activeRole={currentRole}
							onRoleChange={onRoleChange}
							roles={user.roles}
						/>
						<Separator className="my-1" />
					</>
				)}
				{(user.roles.length <= 1 || !onRoleChange) && (
					<Separator className="my-1" />
				)}
			</SidebarHeader>
			<SidebarContent>
				<NavMain items={navItems} />
			</SidebarContent>
			<SidebarFooter>
				<NavUser user={user} />
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}
